import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/ require three levels up to reach convex/_generated/
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import pattern
import { useOrgContext } from '@/hooks/useOrgContext';
import { usePreAuthDetailActions } from '@/hooks/usePreAuthDetailActions';
import {
  ApproveDialog,
  DenyDialog,
  LinkClaimDialog,
} from '@/components/insurance/PreAuthActionDialogs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Check, X, Link } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TPreAuthStatus } from '@/@types/claims';

const STATUS_BADGE: Record<TPreAuthStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  requested: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  under_review: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  approved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  denied: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  expired: 'bg-muted text-muted-foreground',
};

const STATUS_LABELS: Record<TPreAuthStatus, string> = {
  draft: 'Draft',
  requested: 'Requested',
  under_review: 'Under Review',
  approved: 'Approved',
  denied: 'Denied',
  expired: 'Expired',
};

const STATUS_TRANSITIONS: Record<TPreAuthStatus, TPreAuthStatus[]> = {
  draft: [],
  requested: ['under_review'],
  under_review: ['approved', 'denied'],
  approved: ['expired'],
  denied: [],
  expired: [],
};

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
}

function formatDate(ts: number) {
  return new Date(ts).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function InfoRow({ label, value }: { label: string; value: string | undefined | null }) {
  // LOCAL_TYPE_JUSTIFIED: inline prop shape for private helper component, not a shared domain type
  return (
    <div>
      <dt className="text-xs text-muted-foreground uppercase tracking-wide">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium text-foreground">{value ?? '—'}</dd>
    </div>
  );
}

function WizardFormDataCard({ data }: { data: Record<string, unknown> }) {
  // LOCAL_TYPE_JUSTIFIED: inline prop shape for private helper component, not a shared domain type
  const entries = Object.entries(data).filter(([, v]) => v !== null && v !== undefined && v !== '');

  if (entries.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Application Form Data</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
          {entries.map(([key, value]) => {
            const label = key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
            const display = typeof value === 'object' ? JSON.stringify(value) : String(value);
            return (
              <div key={key}>
                <dt className="text-xs text-muted-foreground uppercase tracking-wide capitalize">
                  {label}
                </dt>
                <dd className="mt-0.5 text-sm font-medium text-foreground break-words">
                  {display}
                </dd>
              </div>
            );
          })}
        </dl>
      </CardContent>
    </Card>
  );
}

export function PreAuthDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentOrg } = useOrgContext();

  const preAuthId = id as Id<'preAuthorizations'>; // AS_JUSTIFIED: one-time Id cast from route params at component top
  const preAuth = useQuery(api.insurance.preAuthorizations.get, preAuthId ? { preAuthId } : 'skip');
  const claims = useQuery(
    api.insurance.claims.list,
    preAuth && currentOrg ? { orgId: currentOrg._id } : 'skip'
  );

  const preAuthStatus = (preAuth?.status ?? 'requested') as TPreAuthStatus; // AS_JUSTIFIED: status is a union literal from the Convex return validator, narrowed to TPreAuthStatus for STATUS_BADGE/STATUS_LABELS lookup

  const {
    approveOpen,
    setApproveOpen,
    denyOpen,
    setDenyOpen,
    linkOpen,
    setLinkOpen,
    notes,
    setNotes,
    isSavingNotes,
    isUpdatingStatus,
    handleStatusChange,
    handleSaveNotes,
  } = usePreAuthDetailActions({
    preAuthId,
    currentStatus: preAuthStatus,
    internalNotes: preAuth?.internalNotes,
  });

  if (preAuth === undefined) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (preAuth === null) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Pre-authorization not found.</p>
        <Button variant="ghost" className="mt-3" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
      </div>
    );
  }

  const nextStatuses = STATUS_TRANSITIONS[preAuthStatus];
  const isActionable = preAuthStatus === 'requested' || preAuthStatus === 'under_review';
  type TClaimItem = NonNullable<typeof claims>[number]; // LOCAL_TYPE_JUSTIFIED: local helper type alias derived from useQuery return, not a shared domain type
  const policyFilteredClaims = (claims ?? []).filter(
    (c: TClaimItem) => c.policyId === preAuth.policyId
  );

  const appData = preAuth.preAuthApplicationData as Record<string, unknown> | undefined; // AS_JUSTIFIED: preAuthApplicationData uses the any validator in Convex — cast to Record to check for 'values' key presence
  const rawValues = appData && 'values' in appData ? appData['values'] : undefined;
  const wizardFormValues =
    rawValues && typeof rawValues === 'object' ? (rawValues as Record<string, unknown>) : undefined; // AS_JUSTIFIED: rawValues extracted from untyped Convex field — narrowed via typeof check before cast

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-1.5 h-4 w-4" /> Back
          </Button>
          <h1 className="text-xl font-semibold text-foreground">{preAuth.preAuthNumber}</h1>
          <Badge className={cn('text-xs font-medium capitalize', STATUS_BADGE[preAuthStatus])}>
            {STATUS_LABELS[preAuthStatus]}
          </Badge>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Select
            value=""
            onValueChange={handleStatusChange}
            disabled={isUpdatingStatus || nextStatuses.length === 0}
          >
            <SelectTrigger className="w-48 h-8 text-sm">
              <SelectValue placeholder="Change status..." />
            </SelectTrigger>
            <SelectContent>
              {nextStatuses.map(s => (
                <SelectItem key={s} value={s} className="capitalize">
                  Move to {STATUS_LABELS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {isActionable && (
            <>
              <Button size="sm" variant="outline" onClick={() => setDenyOpen(true)}>
                <X className="mr-1.5 h-3.5 w-3.5" /> Deny
              </Button>
              <Button size="sm" onClick={() => setApproveOpen(true)}>
                <Check className="mr-1.5 h-3.5 w-3.5" /> Approve
              </Button>
            </>
          )}

          {preAuthStatus === 'approved' && (
            <Button size="sm" variant="outline" onClick={() => setLinkOpen(true)}>
              <Link className="mr-1.5 h-3.5 w-3.5" /> Link to Claim
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Pre-Authorization Details</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 lg:grid-cols-4 text-sm">
            <InfoRow label="Claimant ID" value={preAuth.claimantId} />
            <InfoRow label="Claimant Type" value={preAuth.claimantType} />
            <InfoRow label="Treatment Type" value={preAuth.treatmentType} />
            <InfoRow label="Proposed Treatment" value={preAuth.proposedTreatment} />
            <InfoRow label="Proposed Provider" value={preAuth.proposedProvider} />
            <InfoRow
              label="Estimated Cost"
              value={
                preAuth.estimatedCost !== undefined && preAuth.currency
                  ? formatCurrency(preAuth.estimatedCost, preAuth.currency)
                  : preAuth.estimatedCost !== undefined
                    ? String(preAuth.estimatedCost)
                    : undefined
              }
            />
            <InfoRow label="Submitted" value={formatDate(preAuth.createdAt)} />
            {preAuth.medicalJustification && (
              <div className="col-span-2 sm:col-span-3 lg:col-span-4">
                <dt className="text-xs text-muted-foreground uppercase tracking-wide">
                  Medical Justification
                </dt>
                <dd className="mt-0.5 text-sm font-medium text-foreground">
                  {preAuth.medicalJustification}
                </dd>
              </div>
            )}
          </dl>
        </CardContent>
      </Card>

      {preAuthStatus === 'approved' && preAuth.approvedAmount !== undefined && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Approval Details</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 text-sm">
              <InfoRow
                label="Approved Amount"
                value={
                  preAuth.currency
                    ? formatCurrency(preAuth.approvedAmount, preAuth.currency)
                    : String(preAuth.approvedAmount)
                }
              />
              <InfoRow
                label="Valid Until"
                value={preAuth.validUntil ? formatDate(preAuth.validUntil) : undefined}
              />
            </dl>
            {preAuth.linkedClaimId && (
              <>
                <Separator className="my-4" />
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Linked Claim:</span>
                  <Button
                    variant="link"
                    className="h-auto p-0 text-sm"
                    onClick={() => navigate(`/insurance/claims/${preAuth.linkedClaimId}`)}
                  >
                    View Claim
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {preAuthStatus === 'denied' && preAuth.denialReason && (
        <Card className="border-destructive/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-destructive">Denial Reason</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">{preAuth.denialReason}</p>
          </CardContent>
        </Card>
      )}

      {wizardFormValues && <WizardFormDataCard data={wizardFormValues} />}

      {preAuth.supportingDocIds && preAuth.supportingDocIds.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Supporting Documents</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1">
              {preAuth.supportingDocIds.map((docId: string, i: number) => (
                <li key={docId} className="text-sm">
                  <span className="text-muted-foreground">Document {i + 1}: </span>
                  <span className="font-mono text-xs">{docId}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Internal Notes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Visible to org members only — not shown to the claimant.
          </p>
          <Textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Add internal notes about this pre-authorization..."
            rows={4}
          />
          <Button size="sm" onClick={handleSaveNotes} disabled={isSavingNotes}>
            {isSavingNotes ? 'Saving...' : 'Save Notes'}
          </Button>
        </CardContent>
      </Card>

      <ApproveDialog
        preAuthId={preAuth._id}
        estimatedCost={preAuth.estimatedCost ?? 0}
        currency={preAuth.currency ?? 'USD'}
        open={approveOpen}
        onOpenChange={setApproveOpen}
      />
      <DenyDialog preAuthId={preAuth._id} open={denyOpen} onOpenChange={setDenyOpen} />
      <LinkClaimDialog
        preAuthId={preAuth._id}
        claims={policyFilteredClaims}
        open={linkOpen}
        onOpenChange={setLinkOpen}
      />
    </div>
  );
}
